import type { ReactNode } from 'react';
import { Box, Card, CardContent, Skeleton, Stack, Typography } from '@mui/material';
import { LABEL_COLOR, softCardSx } from '../constants';

export interface WidgetCardProps {
    title: string;
    subtitle?: string;
    headline?: ReactNode;
    loading?: boolean;
    children?: ReactNode;
    minHeight?: number;
    action?: ReactNode;
}

export const WidgetCard = ({
    title,
    subtitle,
    headline,
    loading = false,
    children,
    minHeight = 350,
    action,
}: WidgetCardProps) => {
    return (
        <Card
            elevation={0}
            sx={{
                ...softCardSx,
                flex: 1,
                minWidth: 300,
                minHeight,
            }}
        >
            <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                <Box sx={{ mb: 2 }}>
                    <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="flex-start"
                        sx={{ mb: headline ? 1 : 0 }}
                    >
                        <Box>
                            <Typography variant="body2" sx={{ color: LABEL_COLOR, fontWeight: 500 }}>
                                {title}
                            </Typography>
                            {subtitle && (
                                <Typography variant="caption" sx={{ color: LABEL_COLOR, display: 'block' }}>
                                    {subtitle}
                                </Typography>
                            )}
                        </Box>
                        {action}
                    </Stack>
                    {headline && (
                        <Typography
                            component="div"
                            sx={{
                                fontSize: 32,
                                fontWeight: 700,
                                color: '#111827',
                                wordBreak: 'break-word',
                            }}
                        >
                            {headline}
                        </Typography>
                    )}
                </Box>

                {loading ? (
                    <Stack spacing={1}>
                        <Skeleton variant="text" height={28} />
                        <Skeleton variant="text" height={28} />
                        <Skeleton variant="text" height={28} />
                    </Stack>
                ) : (
                    children
                )}
            </CardContent>
        </Card>
    );
};

WidgetCard.displayName = 'WidgetCard';
